"use client";

import { useState, useEffect, useRef, useCallback, ReactElement, createContext, useContext, useMemo } from "react";
import Link from "@/components/LocalizedLink";
import { motion, AnimatePresence } from "framer-motion";
import { SofiaConnectLogo } from "./SofiaConnectLogo";
import { ThemeToggle } from "./ThemeToggle";
import { StatusBadge } from "./StatusBadge";
import { useTranslation, LanguageSwitcher } from "@/lib/i18n";
import { trackEvent } from "@/lib/analytics";

type HeaderNavSurface = "desktop_primary" | "desktop_dropdown" | "mobile_primary" | "mobile_child";

function trackHeaderNav(params: {
  surface: HeaderNavSurface;
  label: string;
  url: string;
  parent?: string;
}): void {
  trackEvent("header_nav_click", {
    surface: params.surface,
    label: params.label,
    url: params.url,
    parent: params.parent ?? "",
  });
}

// ─── Nav data types ───────────────────────────────────────────────────────────

type NavChild = {
  label: string;
  href: string;
  desc?: string;
};

type NavItem = {
  label: string;
  href: string;
  children?: NavChild[];
  desktopOnly?: boolean;
  mobileOnly?: boolean;
};

// ─── Desktop dropdown icons (small SVG glyphs), keyed by href so labels can be translated ─

const iconMap: Record<string, ReactElement> = {
  "/services/ip-transit": (
    <svg viewBox="0 0 16 16" fill="none" className="w-3.5 h-3.5 flex-shrink-0" aria-hidden="true">
      <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="1.4" />
      <path d="M8 2v12M2 8h12" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  ),
  "/services/ethernet-l2vpn": (
    <svg viewBox="0 0 16 16" fill="none" className="w-3.5 h-3.5 flex-shrink-0" aria-hidden="true">
      <rect x="1" y="5" width="14" height="6" rx="1.5" stroke="currentColor" strokeWidth="1.4" />
      <path d="M4 8h8" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  ),
  "/services/spectrum": (
    <svg viewBox="0 0 16 16" fill="none" className="w-3.5 h-3.5 flex-shrink-0" aria-hidden="true">
      <path d="M2 13V9M5.5 13V5M9 13V3M12.5 13V7" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  ),
  "/services/wavelengths-dwdm": (
    <svg viewBox="0 0 16 16" fill="none" className="w-3.5 h-3.5 flex-shrink-0" aria-hidden="true">
      <path d="M1 8 Q4 3 7 8 Q10 13 13 8 Q14.5 5.5 15 8" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" fill="none" />
    </svg>
  ),
  "/services/dark-fiber": (
    <svg viewBox="0 0 16 16" fill="none" className="w-3.5 h-3.5 flex-shrink-0" aria-hidden="true">
      <circle cx="3" cy="8" r="1.5" stroke="currentColor" strokeWidth="1.4" />
      <circle cx="13" cy="8" r="1.5" stroke="currentColor" strokeWidth="1.4" />
      <path d="M4.5 8 Q8 4 11.5 8" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" fill="none" />
    </svg>
  ),
  "/solutions/ai-ready-infrastructure": (
    <svg viewBox="0 0 16 16" fill="none" className="w-3.5 h-3.5 flex-shrink-0" aria-hidden="true">
      <rect x="5" y="2" width="6" height="4" rx="1" stroke="currentColor" strokeWidth="1.4" />
      <rect x="5" y="10" width="6" height="4" rx="1" stroke="currentColor" strokeWidth="1.4" />
      <path d="M8 6v4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
      <path d="M3 8h2M11 8h2" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  ),
  "/network-legacy": (
    <svg viewBox="0 0 16 16" fill="none" className="w-3.5 h-3.5 flex-shrink-0" aria-hidden="true">
      <circle cx="8" cy="8" r="2" stroke="currentColor" strokeWidth="1.4" />
      <circle cx="2.5" cy="4" r="1.2" stroke="currentColor" strokeWidth="1.2" />
      <circle cx="13.5" cy="4" r="1.2" stroke="currentColor" strokeWidth="1.2" />
      <circle cx="2.5" cy="12" r="1.2" stroke="currentColor" strokeWidth="1.2" />
      <circle cx="13.5" cy="12" r="1.2" stroke="currentColor" strokeWidth="1.2" />
      <path d="M6.4 6.8 L3.7 4.8M9.6 6.8 L12.3 4.8M6.4 9.2 L3.7 11.2M9.6 9.2 L12.3 11.2" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
    </svg>
  ),
  "/status": (
    <svg viewBox="0 0 16 16" fill="none" className="w-3.5 h-3.5 flex-shrink-0" aria-hidden="true">
      <circle cx="8" cy="8" r="2" fill="currentColor" />
      <circle cx="8" cy="8" r="5" stroke="currentColor" strokeWidth="1.2" opacity="0.5" />
      <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1" opacity="0.25" />
    </svg>
  ),
  "/looking-glass": (
    <svg viewBox="0 0 16 16" fill="none" className="w-3.5 h-3.5 flex-shrink-0" aria-hidden="true">
      <circle cx="6.5" cy="6.5" r="4" stroke="currentColor" strokeWidth="1.4" />
      <path d="M10 10 L14 14" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  ),
  "/#path-builder": (
    <svg viewBox="0 0 16 16" fill="none" className="w-3.5 h-3.5 flex-shrink-0" aria-hidden="true">
      <circle cx="3" cy="3" r="1.5" stroke="currentColor" strokeWidth="1.2" />
      <circle cx="13" cy="3" r="1.5" stroke="currentColor" strokeWidth="1.2" />
      <circle cx="8" cy="8" r="1.5" stroke="currentColor" strokeWidth="1.2" />
      <circle cx="3" cy="13" r="1.5" stroke="currentColor" strokeWidth="1.2" />
      <circle cx="13" cy="13" r="1.5" stroke="currentColor" strokeWidth="1.2" />
      <path d="M4.5 3h7M3 4.5v7M13 4.5v7M4.5 13h7M4.2 4.2l7.6 7.6M11.8 4.2L4.2 11.8" stroke="currentColor" strokeWidth="1" strokeLinecap="round" opacity="0.5" />
    </svg>
  ),
  "/developers": (
    <svg viewBox="0 0 16 16" fill="none" className="w-3.5 h-3.5 flex-shrink-0" aria-hidden="true">
      <path d="M4 6L1 8l3 2M12 6l3 2-3 2M9.5 4l-3 8" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  "/tools/latency": (
    <svg viewBox="0 0 16 16" fill="none" className="w-3.5 h-3.5 flex-shrink-0" aria-hidden="true">
      <circle cx="8" cy="9" r="5.5" stroke="currentColor" strokeWidth="1.4" />
      <path d="M8 9V5.5M8 9l2.5 2" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
      <path d="M5.5 2h5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  ),
  "/tools/roi-calculator": (
    <svg viewBox="0 0 16 16" fill="none" className="w-3.5 h-3.5 flex-shrink-0" aria-hidden="true">
      <rect x="2" y="2" width="12" height="12" rx="2" stroke="currentColor" strokeWidth="1.4" />
      <path d="M5 11l2-3 2 2 2-4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  "/tools/network-advisor": (
    <svg viewBox="0 0 16 16" fill="none" className="w-3.5 h-3.5 flex-shrink-0" aria-hidden="true">
      <circle cx="8" cy="5" r="3" stroke="currentColor" strokeWidth="1.4" />
      <path d="M2 14c0-3.3 2.7-6 6-6s6 2.7 6 6" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  ),
  "SC-NIA": (
    <svg viewBox="0 0 16 16" fill="none" className="w-3.5 h-3.5 flex-shrink-0" aria-hidden="true">
      <path d="M8 2L9.8 6.2H14L10.6 8.8L11.8 13L8 10.4L4.2 13L5.4 8.8L2 6.2H6.2L8 2Z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
    </svg>
  ),
  "Provisioning Demo": (
    <svg viewBox="0 0 16 16" fill="none" className="w-3.5 h-3.5 flex-shrink-0" aria-hidden="true">
      <polygon points="4,2 12,8 4,14" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
    </svg>
  ),
};

const defaultIcon: ReactElement = (
  <svg viewBox="0 0 16 16" fill="none" className="w-3.5 h-3.5 flex-shrink-0" aria-hidden="true">
    <circle cx="8" cy="8" r="5.5" stroke="currentColor" strokeWidth="1.4" />
    <path d="M8 5v4l2 2" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
  </svg>
);

// ─── Desktop dropdown coordinator + component ─────────────────────────────
// Single source of truth for "which desktop dropdown is currently open".
// Without this, each DesktopDropdown holds its own local `open` state and two
// menus can be open at the same time during fast cursor movement — the 300ms
// close timeout on the leaving menu overlaps with the entering menu's open.
// The coordinator makes opening menu B synchronously close menu A.

type DropdownCoordinator = {
  activeId: string | null;
  openMenu: (id: string) => void;
  requestClose: (id: string) => void;
  closeMenu: (id: string) => void;
  cancelClose: () => void;
};

const DropdownCoordinatorContext = createContext<DropdownCoordinator | null>(null);

function useDropdownCoordinator(): DropdownCoordinator {
  const [activeId, setActiveId] = useState<string | null>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearTimer = useCallback(() => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  }, []);

  const openMenu = useCallback((id: string) => {
    clearTimer();
    setActiveId(id);
  }, [clearTimer]);

  const requestClose = useCallback((id: string) => {
    clearTimer();
    closeTimer.current = setTimeout(() => {
      setActiveId((current) => (current === id ? null : current));
    }, 300);
  }, [clearTimer]);

  // Instant close, no timer. Used by keyboard paths (Escape) where the user
  // has issued an explicit close intent — a 300ms delay after Escape feels
  // broken and prevents predictable focus return.
  const closeMenu = useCallback((id: string) => {
    clearTimer();
    setActiveId((current) => (current === id ? null : current));
  }, [clearTimer]);

  return useMemo(
    () => ({ activeId, openMenu, requestClose, closeMenu, cancelClose: clearTimer }),
    [activeId, openMenu, requestClose, closeMenu, clearTimer],
  );
}

const DROPDOWN_VARIANTS = {
  hidden: (handoff: boolean) => ({
    opacity: 0,
    y: -6,
    transition: { duration: handoff ? 0 : 0.18, ease: "easeOut" as const },
  }),
  shown: { opacity: 1, y: 0, transition: { duration: 0.18, ease: "easeOut" as const } },
};

type DesktopDropdownProps = {
  item: NavItem;
};

function DesktopDropdown({ item }: DesktopDropdownProps) {
  const coordinator = useContext(DropdownCoordinatorContext);
  const menuId = `dropdown-${item.href}`;
  const open = coordinator?.activeId === menuId;
  const handoff = !open && coordinator?.activeId != null;
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const setOpen = useCallback((next: boolean) => {
    if (!coordinator) return;
    if (next) coordinator.openMenu(menuId);
    else coordinator.requestClose(menuId);
  }, [coordinator, menuId]);

  // Instant close for keyboard (Escape) — no 300ms hover-close delay.
  const closeNow = useCallback(() => {
    coordinator?.closeMenu(menuId);
  }, [coordinator, menuId]);

  const handleMouseEnter = () => {
    coordinator?.openMenu(menuId);
  };

  const handleMouseLeave = () => {
    coordinator?.requestClose(menuId);
  };

  // Close on Escape key and return focus to trigger
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === "Escape" && open) {
      e.preventDefault();
      // Instant close on Escape — the hover-close 300ms timer is only for
      // mouse leave, where it gives the user a grace period to re-enter.
      // Keyboard Escape is an explicit intent to close now.
      closeNow();
      triggerRef.current?.focus();
    }
    // Open on Enter/Space for keyboard users
    if ((e.key === "Enter" || e.key === " ") && !open) {
      e.preventDefault();
      setOpen(true);
    }
  }, [open, setOpen, closeNow]);

  return (

    <div
      className="relative"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Trigger */}
      <button
        ref={triggerRef}
        // Signal: 2px corners on nav triggers (was rounded-lg = 8px).
        className="flex items-center gap-1 px-3 py-2 rounded-[2px] text-[13px] font-mono tracking-wider transition-colors duration-150 outline-none"
        onFocus={(e) => (e.currentTarget.style.boxShadow = "0 0 0 2px var(--accent)")}
        onBlur={(e) => (e.currentTarget.style.boxShadow = "none")}
        style={{
          color: open ? "var(--text-primary)" : "var(--text-secondary)",
          background: open ? "var(--hover-bg)" : "transparent",
        }}
        aria-expanded={open}
        aria-haspopup="menu"
        aria-controls={menuId}
        onClick={() => setOpen(!open)}
        onKeyDown={handleKeyDown}
      >
        {item.label}
        <svg
          viewBox="0 0 12 12"
          fill="none"
          className="w-2.5 h-2.5 transition-transform duration-200"
          style={{ transform: open ? "rotate(180deg)" : "rotate(0deg)" }}
          aria-hidden="true"
        >
          <path
            d="M2 4l4 4 4-4"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {/* Dropdown panel. When another menu takes over (handoff), this one
          must disappear at once, otherwise both panels were visible while
          moving from Tools to More. The exit variant drops the fade, and
          this wrapper (outside AnimatePresence, so it always gets fresh
          props) hides a panel whose fade had already started on the close
          timer. display: contents keeps the layout unchanged. */}
      <div style={{ display: "contents", visibility: handoff ? "hidden" : undefined }}>
      <AnimatePresence custom={handoff}>
        {open && item.children && (
          <motion.div
            ref={menuRef}
            id={menuId}
            role="menu"
            aria-label={item.label}
            custom={handoff}
            variants={DROPDOWN_VARIANTS}
            initial="hidden"
            animate="shown"
            exit="hidden"
            // Signal: 2px corners on dropdown panel (was rounded-xl = 12px).
            // Drop the glass-blur so the panel reads as a Signal surface, not
            // a translucent modal. --popup-bg + --popup-border remain
            // theme-aware and keep parity with existing patterns elsewhere.
            className="absolute top-full left-0 mt-2 min-w-[220px] rounded-[2px] overflow-hidden"
            style={{
              background: "var(--popup-bg)",
              border: "1px solid var(--popup-border)",
              boxShadow: "0 16px 48px rgba(0,0,0,0.3), 0 4px 16px rgba(0,0,0,0.2)",
            }}
            onKeyDown={(e) => {
              if (e.key === "Escape") {
                setOpen(false);
                triggerRef.current?.focus();
              }
            }}
          >
            <div className="p-1.5">
              {item.children.map((child) => (
                <Link
                  key={child.href}
                  href={child.href}
                  role="menuitem"
                  onClick={() => {
                    trackHeaderNav({
                      surface: "desktop_dropdown",
                      label: child.label,
                      url: child.href,
                      parent: item.label,
                    });
                    setOpen(false);
                  }}
                  // Signal: 2px corners on dropdown items (was rounded-lg).
                  className="flex items-start gap-2.5 px-3 py-2.5 rounded-[2px] group transition-colors duration-150"
                  style={{ color: "var(--text-secondary)" }}
                  onMouseEnter={(e) => {
                    (e.currentTarget as HTMLElement).style.background = "var(--hover-bg)";
                    (e.currentTarget as HTMLElement).style.color = "var(--text-primary)";
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLElement).style.background = "transparent";
                    (e.currentTarget as HTMLElement).style.color = "var(--text-secondary)";
                  }}
                >
                  <span
                    className="mt-0.5 transition-colors duration-150"
                    style={{ color: "var(--accent)" }}
                    aria-hidden="true"
                  >
                    {iconMap[child.href] ?? defaultIcon}
                  </span>
                  <span className="flex flex-col min-w-0">
                    <span className="text-[12px] font-mono tracking-wider leading-tight">
                      {child.label}
                    </span>
                    {child.desc && (
                      <span
                        className="text-[11px] leading-snug mt-0.5 truncate"
                        style={{ color: "var(--text-muted)" }}
                      >
                        {child.desc}
                      </span>
                    )}
                  </span>
                </Link>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      </div>
    </div>
  );
}

// ─── Main Header ─────────────────────────────────────────────────────────────

export function Header() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { t } = useTranslation();
  const dropdownCoordinator = useDropdownCoordinator();

  // The header renders a desktop nav (hidden md:flex) and a mobile row
  // (flex md:hidden). Both contain a <LanguageSwitcher/>, so at the CSS
  // level only one is ever visible, but the DOM still contains both
  // <button>s. Modern assistive tech honours `display: none` and
  // therefore only sees one, but source-level audits count both and
  // some accessibility scanners do not evaluate media queries. We
  // mirror the CSS-driven visibility with an `aria-hidden` toggle on
  // whichever copy is off-screen so the accessibility tree is
  // unambiguously single. SSR default = desktop visible (matches the
  // initial no-JS render); a client-side matchMedia effect corrects
  // this on mount and on breakpoint changes.
  const [mobileViewport, setMobileViewport] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(max-width: 767px)");
    const sync = () => setMobileViewport(mq.matches);
    sync();
    mq.addEventListener("change", sync);
    return () => mq.removeEventListener("change", sync);
  }, []);

  // Build nav items inside the component so t() is available for descriptions
  const navItems: NavItem[] = [
    {
      label: t("services"),
      href: "/#services",
      children: [
        {
          label: "IP Transit",
          href: "/services/ip-transit",
          desc: t("desc_ip_transit"),
        },
        {
          label: t("prod_l2vpn"),
          href: "/services/ethernet-l2vpn",
          desc: t("desc_l2vpn"),
        },
        {
          label: t("prod_dwdm"),
          href: "/services/wavelengths-dwdm",
          desc: t("desc_dwdm"),
        },
        {
          label: t("prod_spectrum"),
          href: "/services/spectrum",
          desc: t("desc_spectrum"),
        },
        {
          label: t("prod_dark_fiber"),
          href: "/services/dark-fiber",
          desc: t("desc_dark_fiber"),
        },
        {
          label: t("nav_ai_ready"),
          href: "/solutions/ai-ready-infrastructure",
          desc: t("desc_ai_ready"),
        },
        {
          label: t("nav_solution_finder"),
          href: "/solutions/finder",
          desc: t("desc_solution_finder"),
        },
      ],
    },
    {
      label: t("network"),
      href: "/network",
      children: [
        {
          label: t("nav_interactive_map_3d"),
          href: "/network",
          desc: t("nav_interactive_map_3d_desc"),
        },
        {
          label: t("footer_network_status"),
          href: "/status",
          desc: t("desc_network_status"),
        },
        {
          label: t("footer_looking_glass"),
          href: "/looking-glass",
          desc: t("desc_looking_glass"),
        },
        {
          label: t("nav_path_builder"),
          href: "/#path-builder",
          desc: t("desc_network_mesh"),
        },
        {
          label: t("footer_developer_portal"),
          href: "/developers",
          desc: t("desc_developer_portal"),
        },
        {
          label: t("nav_locations"),
          href: "/locations",
          desc: t("nav_locations_desc_full"),
        },
      ],
    },
    {
      label: t("tools"),
      href: "/tools/latency",
      children: [
        {
          label: t("footer_latency_checker"),
          href: "/tools/latency",
          desc: t("desc_latency_checker"),
        },
        {
          label: t("footer_roi_calculator"),
          href: "/tools/roi-calculator",
          desc: t("desc_roi_calculator"),
        },
        {
          label: t("footer_network_advisor"),
          href: "/tools/network-advisor",
          desc: t("desc_network_advisor"),
        },
        {
          label: t("bw_nav_label"),
          href: "/tools/bandwidth-calculator",
          desc: t("desc_bandwidth_calculator"),
        },
        {
          label: "SC-NIA",
          href: "/ai-advisor",
          desc: t("desc_ai_advisor"),
        },
        {
          label: t("footer_provisioning_demo"),
          href: "/demos/provisioning",
          desc: t("desc_provisioning"),
        },
      ],
    },
    // Desktop primary links
    { label: t("naas_platform"), href: "/#configurator" },
    // Secondary links — desktop "More" dropdown / kept in mobile hamburger
    {
      label: t("more"),
      href: "#",
      desktopOnly: true,
      children: [
        { label: t("nav_about"), href: "/about", desc: t("nav_about_desc") },
        { label: t("nav_locations"), href: "/locations", desc: t("nav_locations_desc_short") },
        { label: t("nav_knowledge_base_short"), href: "/blog", desc: t("nav_knowledge_base_desc") },
        { label: t("nav_case_studies"), href: "/case-studies", desc: t("nav_case_studies_desc") },
        { label: t("nav_news_short"), href: "/news", desc: t("nav_news_desc") },
        { label: t("resources"), href: "/resources", desc: t("nav_resources_desc") },
        { label: t("contact"), href: "/contact", desc: t("nav_request_quote") },
      ],
    },
    // These appear in the mobile hamburger but not in the desktop More dropdown
    { label: t("nav_about"), href: "/about", mobileOnly: true },
    { label: t("nav_knowledge_base_short"), href: "/blog", mobileOnly: true },
    { label: t("nav_case_studies"), href: "/case-studies", mobileOnly: true },
    { label: t("nav_news_short"), href: "/news", mobileOnly: true },
    { label: t("resources"), href: "/resources", mobileOnly: true },
    { label: t("contact"), href: "/contact", mobileOnly: true },
  ];

  // Scroll listener for header opacity
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Lock body scroll when mobile menu is open
  // and flag it on <html> so floating buttons hide while it is open.
  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    if (menuOpen) document.documentElement.dataset.mobileMenu = "open";
    else delete document.documentElement.dataset.mobileMenu;
    return () => {
      document.body.style.overflow = "";
      delete document.documentElement.dataset.mobileMenu;
    };
  }, [menuOpen]);

  // Desktop nav items (exclude mobileOnly)
  const desktopPrimary = navItems.filter((i) => !i.mobileOnly);
  // Mobile nav items (exclude desktopOnly)
  const mobileItems = navItems.filter((i) => !i.desktopOnly);

  return (
    <>
      <header
        // Signal treatment: replace the glass blur with a flat Signal surface
        // and a hairline bottom border. Sharp corners, quiet chrome — the
        // NOC aesthetic used across the homepage. `--signal-surface` and
        // `--signal-line` are theme-aware (dark + light values verified).
        className="fixed top-0 left-0 right-0 z-50 transition-colors duration-300"
        style={{
          // --header-top-bg is transparent in dark theme and the surface
          // colour in light theme, where a transparent bar put dark ink
          // over the always-dark /network/ hero.
          background: scrolled
            ? "var(--signal-surface)"
            : "var(--header-top-bg)",
          borderBottom: scrolled
            ? "1px solid var(--signal-line)"
            : "1px solid transparent",
        }}
      >
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex h-16 items-center justify-between">

            {/* ── Logo ─────────────────────────────────────────────── */}
            <Link
              href="/"
              className="flex items-center gap-2.5 group flex-shrink-0"
              onClick={() => setMenuOpen(false)}
            >
              {/* Signal: replace the hardcoded blue logo colour with the site
                  accent (teal, theme-aware via --accent) so the brand mark
                  sits in the same colour family as the homepage. Logo uses
                  fill="currentColor", so a text colour on className carries. */}
              <SofiaConnectLogo
                className="w-8 h-8 text-[color:var(--accent)] transition-colors duration-300"
                aria-label="Sofia Connect"
              />
              {/* Signal: brand wordmark in Unbounded (--font-display). Weight
                  600 matches the homepage H2 pairing; letter-spacing is set
                  by .font-display's -0.01em. */}
              <span
                className="font-display text-[15px] transition-colors duration-300"
                style={{
                  color: "var(--logo-text-color)",
                  fontWeight: 600,
                }}
              >
                Sofia Connect
              </span>
            </Link>

            {/* ── Desktop nav ──────────────────────────────────────── */}
            <DropdownCoordinatorContext.Provider value={dropdownCoordinator}>
            <nav
              className="hidden md:flex items-center gap-1"
              aria-label="Primary navigation"
            >
              {desktopPrimary.map((item) => {
                // Items with children → dropdown
                if (item.children) {
                  return <DesktopDropdown key={item.href} item={item} />;
                }
                // NaaS Platform — plain link
                if (item.href === "/#configurator") {
                  return (
                    <Link
                      key={item.href}
                      href={item.href}
                      onClick={() =>
                        trackHeaderNav({
                          surface: "desktop_primary",
                          label: item.label,
                          url: item.href,
                        })
                      }
                      // Signal: 2px corners on the plain NaaS Platform link.
                      className="px-3 py-2 rounded-[2px] text-[13px] font-mono tracking-wider transition-colors duration-150"
                      style={{ color: "var(--text-secondary)" }}
                      onMouseEnter={(e) => {
                        (e.currentTarget as HTMLElement).style.color = "var(--text-primary)";
                        (e.currentTarget as HTMLElement).style.background = "var(--hover-bg)";
                      }}
                      onMouseLeave={(e) => {
                        (e.currentTarget as HTMLElement).style.color = "var(--text-secondary)";
                        (e.currentTarget as HTMLElement).style.background = "transparent";
                      }}
                    >
                      {item.label}
                    </Link>
                  );
                }
                return null;
              })}

              {/* Divider */}
              <span
                className="w-px h-5 mx-2"
                style={{ background: "var(--divider)" }}
                aria-hidden="true"
              />

              {/* Live network status badge */}
              <StatusBadge />

              {/* Theme toggle */}
              <ThemeToggle />

              {/* Language switcher (desktop copy) */}
              <div aria-hidden={mobileViewport}>
                <LanguageSwitcher />
              </div>

              {/* Primary call to action */}
              <Link
                href="/contact"
                onClick={() =>
                  trackHeaderNav({ surface: "desktop_primary", label: "request_quote", url: "/contact" })
                }
                className="ml-2 inline-flex h-9 items-center rounded-[2px] btn-primary px-3.5 text-[13px] font-semibold transition-colors whitespace-nowrap"
              >
                {t("nav_request_quote")}
              </Link>
            </nav>
            </DropdownCoordinatorContext.Provider>

            {/* ── Mobile: language + prominent hamburger ─── */}
            <div className="flex md:hidden items-center gap-2">
              {/* Language switcher (mobile copy) */}
              <div aria-hidden={!mobileViewport}>
                <LanguageSwitcher />
              </div>

              <button
                onClick={() => setMenuOpen(!menuOpen)}
                // Signal: 2px corners on the mobile hamburger (was rounded-xl).
                className="relative w-11 h-11 flex items-center justify-center rounded-[2px] transition-all duration-200 active:scale-95"
                style={{
                  background: menuOpen
                    ? "var(--accent)"
                    : "var(--accent-muted)",
                  border: "1px solid var(--accent)",
                }}
                aria-label={menuOpen ? t("nav_menu_close") : t("nav_menu_open")}
                aria-expanded={menuOpen}
                aria-controls="mobile-nav"
              >
                <div className="flex flex-col gap-[5px]">
                  <motion.span
                    animate={menuOpen ? { rotate: 45, y: 3.5 } : { rotate: 0, y: 0 }}
                    className="block w-[20px] h-[2.5px] origin-center rounded"
                    style={{ background: menuOpen ? "#000" : "var(--accent)" }}
                    transition={{ duration: 0.2 }}
                  />
                  <motion.span
                    animate={menuOpen ? { rotate: -45, y: -3.5 } : { rotate: 0, y: 0 }}
                    className="block w-[20px] h-[2.5px] origin-center rounded"
                    style={{ background: menuOpen ? "#000" : "var(--accent)" }}
                    transition={{ duration: 0.2 }}
                  />
                </div>
              </button>
            </div>

          </div>
        </div>
      </header>

      {/* ── Mobile menu ───────────────────────────────────────────────────
          Full-height panel under the header, scrolls on its own so every
          link is reachable on short screens (390×640). Body scroll is
          locked while it is open (effect above). */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-x-0 bottom-0 top-16 z-40 overflow-y-auto overscroll-contain md:hidden"
            style={{
              background: "var(--overlay-bg)",
              backdropFilter: "blur(24px)",
              paddingBottom: "calc(2rem + env(safe-area-inset-bottom))",
            }}
            role="dialog"
            aria-modal="true"
            aria-label={t("nav_mobile_dialog")}
            onKeyDown={(e) => {
              if (e.key === "Escape") setMenuOpen(false);
            }}
          >
            <nav id="mobile-nav" className="mx-auto flex max-w-md flex-col px-5 pt-4" aria-label="Mobile navigation">
              <Link
                href="/contact"
                onClick={() => {
                  trackHeaderNav({ surface: "mobile_primary", label: "request_quote", url: "/contact" });
                  setMenuOpen(false);
                }}
                className="mb-4 flex min-h-[48px] items-center justify-center rounded-[2px] btn-primary px-5 text-base font-semibold"
              >
                {t("nav_request_quote")}
              </Link>
              {mobileItems.map((item) => (
                <div key={item.href + (item.mobileOnly ? "-mob" : "")} className="border-b border-[var(--card-border)] py-2">
                  <Link
                    href={item.href}
                    onClick={() => {
                      trackHeaderNav({ surface: "mobile_primary", label: item.label, url: item.href });
                      setMenuOpen(false);
                    }}
                    className="flex min-h-[44px] items-center text-xl font-medium"
                    style={{ color: "var(--text-primary)" }}
                  >
                    {item.label}
                  </Link>
                  {item.children && (
                    <ul className="mb-1 grid grid-cols-1 gap-x-4 sm:grid-cols-2">
                      {item.children.map((child) => (
                        <li key={child.href + child.label}>
                          <Link
                            href={child.href}
                            onClick={() => {
                              trackHeaderNav({
                                surface: "mobile_child",
                                label: child.label,
                                url: child.href,
                                parent: item.label,
                              });
                              setMenuOpen(false);
                            }}
                            className="flex min-h-[44px] items-center text-[15px]"
                            style={{ color: "var(--text-secondary)" }}
                          >
                            {child.label}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
              <Link
                href="/portal"
                onClick={() => setMenuOpen(false)}
                className="mt-4 flex min-h-[44px] items-center text-[15px]"
                style={{ color: "var(--text-secondary)" }}
              >
                {t("footer_client_portal")} →
              </Link>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
